import React, { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconName, IconColor } from "@/shared/ui/Icon";

export type PillColor =
  | "green"
  | "primary"
  | "yellow"
  | "red"
  | "purple"
  | "blue"
  | "grey";

interface PillProps extends Omit<React.HTMLProps<HTMLDivElement>, "children"> {
  variant: "filled" | "outlined";
  color: PillColor;
  label?: string;
  icon?: IconName;
  children?: React.ReactNode;
}

const pillStyles = cva(
  "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
  {
    variants: {
      variant: {
        filled: "",
        outlined: "",
      },
      color: {
        green: "bg-green-500 text-white",
        primary: "bg-primary text-white",
        yellow: "bg-orange-500 text-white",
        red: "bg-red-500 text-white",
        purple: "bg-purple-500 text-white",
        blue: "bg-blue-500 text-white",
        grey: "bg-grey-500 text-white",
      },
    },
    compoundVariants: [
      {
        variant: "outlined",
        color: "green",
        class:
          "bg-transparent border border-solid border-green-500 text-green-500",
      },
      {
        variant: "outlined",
        color: "primary",
        class: "bg-transparent border border-solid border-primary text-primary",
      },
      {
        variant: "outlined",
        color: "yellow",
        class:
          "bg-transparent border border-solid border-orange-500 text-orange-500",
      },
      {
        variant: "outlined",
        color: "red",
        class: "bg-transparent border border-solid border-red-500 text-red-500",
      },
      {
        variant: "outlined",
        color: "purple",
        class:
          "bg-transparent border border-solid border-purple-500 text-purple-500",
      },
      {
        variant: "outlined",
        color: "blue",
        class:
          "bg-transparent border border-solid border-blue-500 text-blue-500",
      },
      {
        variant: "outlined",
        color: "grey",
        class:
          "bg-transparent border border-solid border-grey-500 text-grey-500",
      },
    ],
    defaultVariants: {
      variant: "filled",
      color: "primary",
    },
  }
);

/**
 * Determines the appropriate icon color based on pill variant and color
 */
const getPillIconColor = (
  variant: "filled" | "outlined",
  color: PillColor
): IconColor => {
  if (variant === "filled") {
    return "white";
  }

  // For outlined variant, icon color matches the border/text color
  const colorMap: Record<PillColor, IconColor> = {
    green: "green",
    primary: "primary",
    yellow: "orange", // yellow maps to orange for icon
    red: "red",
    purple: "purple",
    blue: "blue",
    grey: "grey",
  };

  return colorMap[color] || "primary";
};

const Pill = forwardRef<HTMLDivElement, PillProps>((props, ref) => {
  const { variant, color, label, icon, children, className, ...rest } = props;

  // Support both label prop and children for flexibility
  const content = label || children;
  if (!content) return null;

  const iconColor = getPillIconColor(variant, color);

  return (
    <div
      ref={ref}
      className={cn(pillStyles({ variant, color }), className)}
      {...rest}
    >
      {icon && (
        <Icon icon={icon} size="xs" color={iconColor} className="mr-1" />
      )}
      {label ? <span>{label}</span> : children}
    </div>
  );
});

export { Pill };
